import clsx from 'clsx';
import Icon from 'components/atoms/Icon';
import { EnglishProficiencyAssetKind } from 'generated/graphql';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  ModalSize,
  RadioButtonGroup,
} from '@spotted-zebra-uk/ui-components';
import EnglishProficiencyAudioPlayer from './EnglishProficiencyAudioPlayer';
import {
  isMultipleChoiceQuestion,
  isQuestionAnswered,
  isShortAnswerQuestion,
  splitOnBlanks,
  TEnglishProficiencyAnswer,
  TEnglishProficiencyQuestion,
} from './EnglishProficiencyTest.helpers';
import styles from './EnglishProficiencyQuestionCard.module.scss';

/** How short a gap is allowed to get. Wide enough to read as a missing word
 * rather than a hyphen, and to hold the placeholder without clipping it. */
const MIN_BLANK_CHARACTERS = 14;

/**
 * The items whose picture needs the room.
 *
 * Less a style choice than a reading of the content. The bank ships its images
 * at two sizes and the size is the author's signal of what the picture is for:
 * the 300px ones are a single object to name ("This is a ___") and the 540px
 * ones are signs and scenes with words to read ("What time does the zoo
 * close?", "What doesn't the hotel have?"). One wants to be small, the other
 * wants to be legible.
 *
 * Keyed by item index because that is the bank's stable identity and this bank
 * is a fixed purchased set. It is still content knowledge living in the client:
 * the durable fix is `width`/`height` on `EnglishProficiencyAsset`, which would
 * let the box be reserved from the asset rather than from a list here. Worth
 * doing if a second item set ever arrives.
 */
const LARGE_IMAGE_QUESTION_INDEXES = new Set([9, 10, 19, 20]);

/** Both are square, matching every image in the bank. The box is fixed rather
 * than fluid so it is reserved before the image lands — a card that grows
 * mid-sitting moves the options under a click already on its way down. */
const IMAGE_SIZE_PX = { small: 200, large: 380 };

interface IEnglishProficiencyQuestionCard {
  question: TEnglishProficiencyQuestion;
  answer: TEnglishProficiencyAnswer | undefined;
  /** The item's place in the form, used for the visible "Question n of 60". */
  position: number;
  total: number;
  onChooseAnswer: (questionIndex: number, selectedAnswerIndex: number) => void;
  onTypeBlanks: (questionIndex: number, blankAnswers: string[]) => void;
  /** Called when a typed answer loses focus, so it is not left only to the
   * debounce if the candidate walks away mid-page. */
  onBlurBlanks: (questionIndex: number) => void;
  disabled: boolean;
}

/**
 * One item, whichever kind it is.
 *
 * The answer goes in the gap, both ways round: a chosen option is written into
 * the sentence, and a typed one is typed into it. A sixty-item page is read as
 * much as it is answered, so an item the candidate scrolls back past should
 * read as the finished sentence they meant, not as a gap with a marked radio
 * button somewhere beneath it.
 *
 * The item's CEFR `category` and `tags` are deliberately not shown: they say
 * how hard the question is meant to be, which is not the candidate's business
 * while they are answering it.
 */
const EnglishProficiencyQuestionCard: FC<IEnglishProficiencyQuestionCard> = ({
  question,
  answer,
  position,
  total,
  onChooseAnswer,
  onTypeBlanks,
  onBlurBlanks,
  disabled,
}) => {
  const { t } = useTranslation();

  // Blanks are a gap in the sentence: render the pieces either side of each
  // marker, and the answer — or a line waiting for one — in between.
  //
  // Both kinds can carry gaps: the marker is on the base question, not on the
  // short-answer one. Most multiple-choice items are cloze items whose options
  // are the candidates for the gap.
  const textSegments = useMemo(
    () => splitOnBlanks(question.text),
    [question.text]
  );
  const gapCount = textSegments.length - 1;

  const blankAnswers = answer?.blankAnswers ?? [];
  const isAnswered = isQuestionAnswered(question, answer);

  const [isImageEnlarged, setIsImageEnlarged] = useState(false);
  const imageSizePx = LARGE_IMAGE_QUESTION_INDEXES.has(question.index)
    ? IMAGE_SIZE_PX.large
    : IMAGE_SIZE_PX.small;
  const imageAlt = t('englishProficiencyTest.questionImageAlt', {
    questionNumber: position,
  });

  // The chosen option, written out. Only when the sentence has exactly one gap
  // — with more than one there is no way to tell which the option belongs in.
  const chosenAnswerText =
    isMultipleChoiceQuestion(question) &&
    answer?.selectedAnswerIndex != null &&
    gapCount === 1
      ? question.answers.find(
          option => option.index === answer.selectedAnswerIndex
        )?.text
      : undefined;

  const handleBlankChange = (blankIndex: number, value: string) => {
    if (!isShortAnswerQuestion(question)) {
      return;
    }
    const next = Array.from(
      { length: question.blankCount },
      (_unused, index) => blankAnswers[index] ?? ''
    );
    next[blankIndex] = value;
    onTypeBlanks(question.index, next);
  };

  const blankLabel = (blankIndex: number, blankCount: number) =>
    blankCount > 1
      ? t('englishProficiencyTest.blankLabelNumbered', {
          number: blankIndex + 1,
        })
      : t('englishProficiencyTest.blankLabel');

  /** Grows with its contents, so a long answer is not read through a scrolling
   * two-inch window. Shared, so the two kinds of gap stay the same size. */
  const blankWidth = (value: string) =>
    `${Math.max(MIN_BLANK_CHARACTERS, value.length + 1)}ch`;

  const renderBlankInput = (blankIndex: number, blankCount: number) => {
    const value = blankAnswers[blankIndex] ?? '';
    return (
      <input
        key={`${question.index}-blank-${blankIndex}`}
        type="text"
        className={styles.blankInput}
        style={{ width: blankWidth(value) }}
        value={value}
        onChange={event => handleBlankChange(blankIndex, event.target.value)}
        onBlur={() => onBlurBlanks(question.index)}
        disabled={disabled}
        // An empty typed gap and an empty chosen one are drawn the same line;
        // the placeholder is what says which of the two this is.
        placeholder={t('englishProficiencyTest.typeHere')}
        aria-label={blankLabel(blankIndex, blankCount)}
        autoComplete="off"
        // Browser assistance would be answering the question for them.
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
      />
    );
  };

  /**
   * The chosen option, shown in the same field a typed answer is typed into —
   * held read-only, because the radio group is what changes it.
   *
   * Sharing the field is the point. An empty inline element has no content box,
   * so its baseline sits somewhere else than a filled one's: swapping a bare
   * line for a span of text moved the gap down the moment an option was chosen.
   * An input keeps its box whatever is in it, so choosing an answer now changes
   * a value and nothing else.
   *
   * Decorative: it repeats what the radio group already says, so it is hidden
   * from screen readers and taken out of the tab order rather than adding sixty
   * more stops that lead nowhere.
   */
  const renderChosenGap = () => (
    <input
      key={`${question.index}-chosen`}
      type="text"
      className={clsx(styles.blankInput, styles.blankInputChosen)}
      style={{ width: blankWidth(chosenAnswerText ?? '') }}
      value={chosenAnswerText ?? ''}
      readOnly
      tabIndex={-1}
      aria-hidden="true"
    />
  );

  const renderGap = (gapIndex: number) =>
    isShortAnswerQuestion(question)
      ? renderBlankInput(gapIndex, question.blankCount)
      : renderChosenGap();

  // A short answer whose text carries fewer markers than it has gaps would
  // otherwise be unanswerable. The bank validator rules that out, but a content
  // change should not be able to strand a candidate on one question.
  const strandedBlanks = isShortAnswerQuestion(question)
    ? Array.from(
        { length: Math.max(0, question.blankCount - gapCount) },
        (_unused, index) => gapCount + index
      )
    : [];

  return (
    <li
      className={styles.card}
      id={`english-proficiency-question-${question.index}`}
      data-testid={`english-proficiency-question-${question.index}`}
    >
      <div className={styles.header}>
        <p className={styles.position}>
          {t('englishProficiencyTest.questionPosition', { position, total })}
        </p>
        {isAnswered ? (
          <span
            className={styles.answeredMark}
            data-testid={`english-proficiency-answered-${question.index}`}
          >
            <Icon icon="check" aria-hidden="true" />
            <span className={styles.answeredMarkLabel}>
              {t('englishProficiencyTest.answered')}
            </span>
          </span>
        ) : null}
      </div>

      {question.passage ? (
        <div className={styles.passage}>
          <h3 className={styles.passageTitle}>{question.passage.title}</h3>
          {question.passage.body
            .split('\n\n')
            .map((paragraph, paragraphIndex) => (
              <p
                // Paragraphs have no identity of their own; their position in
                // a passage that never changes within a sitting is stable.
                key={`${question.index}-paragraph-${paragraphIndex}`}
                className={styles.passageParagraph}
              >
                {paragraph}
              </p>
            ))}
        </div>
      ) : null}

      {question.asset?.kind === EnglishProficiencyAssetKind.Audio ? (
        <EnglishProficiencyAudioPlayer
          src={question.asset.url}
          questionIndex={question.index}
        />
      ) : null}

      {question.asset?.kind === EnglishProficiencyAssetKind.Image ? (
        <div className={styles.imageRow}>
          <img
            className={styles.image}
            style={{ width: imageSizePx, height: imageSizePx }}
            src={question.asset.url}
            alt={imageAlt}
            loading="lazy"
            onClick={() => setIsImageEnlarged(true)}
          />
          <button
            type="button"
            className={styles.enlargeButton}
            onClick={() => setIsImageEnlarged(true)}
            aria-label={t('englishProficiencyTest.viewImageLarger', {
              questionNumber: position,
            })}
            data-testid={`english-proficiency-enlarge-${question.index}`}
          >
            <Icon icon="zoom_in" aria-hidden="true" />
          </button>
        </div>
      ) : null}

      <p className={styles.instructions}>{question.instructions}</p>

      <p className={styles.text}>
        {textSegments.map((segment, segmentIndex) => (
          // Segments are the fixed halves of a fixed sentence; index is their
          // only identity and it cannot shift.
          <span key={`${question.index}-segment-${segmentIndex}`}>
            {segment}
            {segmentIndex < gapCount ? renderGap(segmentIndex) : null}
          </span>
        ))}
        {strandedBlanks.map(blankIndex =>
          isShortAnswerQuestion(question)
            ? renderBlankInput(blankIndex, question.blankCount)
            : null
        )}
      </p>

      {isShortAnswerQuestion(question) ? (
        <p className={styles.clue}>
          {t('englishProficiencyTest.clue', { clue: question.clue })}
        </p>
      ) : null}

      {isMultipleChoiceQuestion(question) ? (
        <RadioButtonGroup
          id={`english-proficiency-${question.index}-answers`}
          name={`english-proficiency-${question.index}-answers`}
          ariaLabel={t('englishProficiencyTest.answersFor', {
            questionNumber: position,
          })}
          className={styles.answers}
          radioButtonClassName={styles.answer}
          disabled={disabled}
          options={question.answers.map(option => ({
            // The option's position in the bank, never on screen: the order
            // here is shuffled per sitting, and this is what gets stored.
            id: `english-proficiency-${question.index}-answer-${option.index}`,
            label: option.text,
            value: String(option.index),
          }))}
          value={
            answer?.selectedAnswerIndex != null
              ? String(answer.selectedAnswerIndex)
              : undefined
          }
          onChange={value => onChooseAnswer(question.index, Number(value))}
        />
      ) : null}

      {isImageEnlarged && question.asset ? (
        <Modal
          isOpen
          onClose={() => setIsImageEnlarged(false)}
          modalSize={ModalSize.LARGE}
          showCloseIcon
          header={t('englishProficiencyTest.questionPosition', {
            position,
            total,
          })}
          className={styles.enlargedModal}
        >
          <img
            className={styles.enlargedImage}
            src={question.asset.url}
            alt={imageAlt}
          />
        </Modal>
      ) : null}
    </li>
  );
};

export default EnglishProficiencyQuestionCard;
